import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, tap, withLatestFrom } from 'rxjs/operators';

import { filter as _filter, isEqual as _isEqual } from 'lodash-es';

import { AuthService } from '@auth/auth.service';
import { selectAuthData } from '@auth/store/auth.selectors';
import { orderFilterSelectors } from '@store/pcache/pcache.selectors';

import { environment } from '@environment';

import * as dataActions from '../data/store/data.actions';
import { ListEntry } from './list-entry';
import * as staticLists from './static.lists';
import * as dataSelectors from './store/data.selectors';

@Injectable()
export class DataService {
  private _listEndpoint = environment.listEndpoint;
  private _customerListEndpoint = environment.customerListEndpoint;

  facilityList$ = this.getListObservable('facility');
  customerList$ = this.getListObservable('customer');
  yesNoList$ = this.getListObservable('yesNo');
  orderPriorityList$ = this.getListObservable('order-priority');
  shipmentTermsList$ = this.getListObservable('shipment-terms');
  stateList$ = this.getListObservable('state-or-province');
  countryCodeList$ = this.getListObservable('country-codes');
  carrierList$ = this.getListObservable('carriers');
  shipmentTypesList$ = this.getListObservable('shipment-types');

  productGroupList$ = this.getCustomerListObservable('product-groups');
  itemsList$ = this.getCustomerListObservable('items');
  shipperList$ = this.getCustomerListObservable('shipper');
  billtoList$ = this.getCustomerListObservable('billto');
  shiptoList$ = this.getCustomerListObservable('shipto');
  passthruList$ = this.getCustomerListObservable('passthru-labels');
  posList$ = this.getCustomerListObservable('pos');
  referencesList$ = this.getCustomerListObservable('references');

  // This are behaviour subjects for common screen functions
  public hideScreen$ = new BehaviorSubject<boolean>(false);
  public hideFullScreen = new BehaviorSubject<boolean>(false);
  public hideFullScreen$: Observable<boolean>;

  public cacheData$ = new BehaviorSubject<boolean>(false);
  public saveData$ = new BehaviorSubject<boolean>(false);
  public saveable$ = new BehaviorSubject<boolean>(false);

  public toggleVisibility$ = new BehaviorSubject<any>(null);

  public resetFormData$ = new BehaviorSubject<any>(null);
  public columnRefresh$ = new BehaviorSubject<any>(null);
  public screenFilter$ = new BehaviorSubject<any>(null);

  public selectedRow$ = new BehaviorSubject<any>(null);
  public selectedIdx$ = new BehaviorSubject<any>(null);
  public selectedDtlRow$ = new BehaviorSubject<any>(null);

  public columnsChanged = new BehaviorSubject<any>(null);
  public columnsChanged$: Observable<any>;

  public gridState$ = new BehaviorSubject<string>(null);

  public screenOffset: number;
  public windowInnerHeight = new BehaviorSubject<number>(window.innerHeight);
  public windowInnerWidth = new BehaviorSubject<number>(window.innerWidth);
  public wsScreenAdjust = new BehaviorSubject<number>(0);
  public wsScreenAdjust$: Observable<any>;

  public showLoadingIndicator$ = new BehaviorSubject<boolean>(false);
  public showSubLoadingIndicator$ = new BehaviorSubject<boolean>(false);
  public showModalLoadingIndicator$ = new BehaviorSubject<boolean>(false);

  public showReportParametersDialog$ = new BehaviorSubject<boolean>(false);

  public globalModalDialogDisplay$ = new BehaviorSubject<boolean>(false);
  public globalModalDialogHeader$ = new BehaviorSubject<string>(null);
  public globalModalDialogMessage$ = new BehaviorSubject<any>(null);

  savedOrderFilters$ = this.store.select(orderFilterSelectors.selectIds);

  constructor(private http: HttpClient, private auth: AuthService, private store: Store) {
    this.hideFullScreen$ = this.hideFullScreen.pipe(tap((hide) => this.hideScreen$.next(hide)));

    this.wsScreenAdjust$ = this.wsScreenAdjust.pipe(
      map((x) => {
        if (!x || x < 0) {
          return {};
        }
        return { height: `calc(100vh + ${x}rem)` };
      })
    );
  }

  init(screenOffset: number = -1) {
    this.screenOffset = screenOffset;
    this.windowInnerHeight.next(window.innerHeight);
    this.windowInnerWidth.next(window.innerWidth);
    this.wsScreenAdjust.next(screenOffset);
    this.hideFullScreen.next(false);
    this.hideScreen$.next(false);
    this.cacheData$.next(false);
    this.saveData$.next(false);
    this.saveable$.next(false);
    this.toggleVisibility$.next(null);
    this.resetFormData$.next(null);
    this.screenFilter$.next(null);
    this.columnsChanged.next(null);

    this.showLoadingIndicator$.next(false);
    this.showSubLoadingIndicator$.next(false);
    this.showModalLoadingIndicator$.next(false);

    this.gridState$.next(null);

    this.globalModalDialogDisplay$.next(false);
    this.globalModalDialogHeader$.next(null);
    this.globalModalDialogMessage$.next(null);

    this.columnsChanged$ = this.columnsChanged.pipe(
      debounceTime(300),
      distinctUntilChanged((x, y) => _isEqual(x, y))
    );
  }

  windowInnerHeight$(offset: number = 0): Observable<any> {
    return this.windowInnerHeight.pipe(
      filter((x) => !!x),
      distinctUntilChanged(),
      map((x) => x - offset),
      map((x) => {
        return { height: `${x}px` };
      })
    );
  }

  deliveryService$(carrier: string) {
    return this.getListObservable('delivery-service').pipe(
      map((x: any) => {
        return { id: carrier, list: _filter(x.list, { carrier }) };
      })
    );
  }

  windowInnerHeightVH$(vh: number, offset: number): Observable<any> {
    return this.windowInnerHeight.pipe(
      filter((x) => !!x),
      distinctUntilChanged(),
      map((x) => Math.round(x * vh - offset)),
      map((x) => {
        return { height: `${x}px` };
      })
    );
  }

  windowInnerHeightValue$(offset: number): Observable<any> {
    return this.windowInnerHeight.pipe(
      filter((x) => !!x),
      distinctUntilChanged(),
      map((x) => x - offset)
    );
  }

  windowInnerWidth$(offset: number): Observable<any> {
    return this.windowInnerWidth.pipe(
      filter((x) => !!x),
      distinctUntilChanged(),
      map((x) => x - offset),
      map((x) => {
        return { width: `${x}px` };
      })
    );
  }

  windowInnerWidthValue$(offset: number): Observable<any> {
    return this.windowInnerWidth.pipe(
      filter((x) => !!x),
      distinctUntilChanged(),
      map((x) => x - offset)
    );
  }

  getListObservable(list: string) {
    return this.store.select(dataSelectors.selectListById(list)).pipe(
      map((x) => (x ? x : new ListEntry())),
      withLatestFrom(this.auth.authData$),
      tap(([listEntry, authData]) => {
        if (!listEntry.id && authData) {
          this.store.dispatch(new dataActions.ApiRequestList(list));
        }
      }),
      filter(([listEntry, _authData]) => !!listEntry.id),
      map(([listEntry, _authData]) => {
        return listEntry;
      })
    );
  }

  public getCustomerListObservable(list: string) {
    return this.store.select(dataSelectors.selectCustomerListById(list)).pipe(
      map((x) => (x ? x : new ListEntry())),
      withLatestFrom(this.auth.authData$),
      tap(([listEntry, authData]) => {
        if (!listEntry.id && authData) {
          this.store.dispatch(new dataActions.ApiRequestCustomerList(list));
        }
      }),
      map(([listEntry, _authData]) => {
        return listEntry;
      })
    );
  }

  getList(listName: string): Observable<ListEntry> {
    const authData$ = this.store.select(selectAuthData);

    if (listName === 'yesNo') {
      return of(new ListEntry(listName, staticLists.yesNoList.slice(0), Date.now()));
    }

    return this.http.get(this._listEndpoint + listName, { withCredentials: true }).pipe(
      map((response: any) => response.list),
      withLatestFrom(authData$),
      map(([list, authData]) => {
        let newList = list;
        const facilities = authData ? authData.facilities : ['NOAUTH'];
        const customers = authData ? authData.customers : ['NOAUTH'];

        if (listName === 'facility') {
          newList = this.filterList(list, facilities, 'ALLFAC');
        } else if (listName === 'customer') {
          newList = this.filterList(list, customers, 'ALLCUST');
        }
        return newList;
      }),
      map((list) => new ListEntry(listName, list, Date.now()))
    );
  }

  filterList(sourceList: any[], filterList: any[], allWord: string): any[] {
    if (!filterList || filterList.length === 0) {
      return sourceList;
    }

    if (allWord !== null && filterList[0] === allWord) {
      return sourceList;
    }

    const newList = _filter(sourceList, (flter: any) => {
      return filterList.indexOf(flter.key) >= 0;
    });

    return newList;
  }

  getCustomerList(custid: string, listName: string): Observable<ListEntry> {
    return this.http.post(`${this._customerListEndpoint}${listName}`, { custid }, { withCredentials: true }).pipe(
      map((response: any) => response.list),
      map((list) => new ListEntry(listName, list, Date.now()))
    );
  }

  getImage(type: string) {
    return this.http.get(`${environment.getImageEndpoint}${type}`, {
      observe: 'response',
      withCredentials: false,
      responseType: 'blob',
    });
  }

  uploadImage(data: any) {
    return this.http.put(environment.uploadImageEndpoint, data, { withCredentials: true });
  }

  deleteImage(type: string) {
    return this.http.post(environment.deleteImageEndpoint, { type }, { withCredentials: true });
  }

  getApiInfo() {
    return this.http.get(environment.infoEndpoint);
  }

  submitErrorReport(data: any, authData: any, selectedRow: any) {
    if (data.statusCode < 500) {
      return of([]);
    }

    const payload: any = {};
    const localStorageJson = {};
    const sessionStorageJson = {};

    for (const key of Object.keys(localStorage)) {
      localStorageJson[key] = JSON.parse(localStorage[key]);
    }

    for (const key of Object.keys(sessionStorage)) {
      sessionStorageJson[key] = JSON.parse(sessionStorage[key]);
    }

    payload.request_id = data.requestId;
    payload.error_code = data.errorCode;
    payload.error_type = data.type;
    payload.url = data.url;
    payload.method = data.req?.method;
    payload.status = data.statusCode;
    payload.error_message = data.message;
    payload.nameid = authData?.user;
    payload.custid = authData?.custid;
    payload.facility = authData?.facility;
    payload.user_agent = navigator.userAgent;
    payload.request_body = JSON.stringify(data.req.body);
    payload.local_storage = JSON.stringify(localStorageJson);
    payload.session_storage = JSON.stringify(sessionStorageJson);
    payload.selected_row = JSON.stringify(selectedRow);
    payload.auth_payload = JSON.stringify(authData);

    return this.http.post(environment.errorReportEndpoint, { ...payload });
  }
}
